// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Modal from '../Modal';
import Loading from 'react-loading';
import api from '../../../api';
import { toast } from 'react-toastify';

// components
import Sortable from '../../Sortable';
import Button from '../../Settings/PlanEditor/Button';
import download from '../../../utilities/downloadable';
import Select from 'react-select';
import Filter from './Filter';
import PyeCalendarSelector from '../../Settings/PlanEditor/PlanCalendarSelect';

// styles
import '../../../styles/automated-reports.css';
import '../../../styles/config.css';

const CustomOption = props => {
    const { innerProps, data } = props;
    return (
        <div {...innerProps}>
            <div
                className={"config-user-user"}
                // onClick={() => onChange(user._id)}
                title={data.label}
            >
                <div className={"config-user-icon palette-color-5"}>
                    {data.label ? data.label[0].toUpperCase() : "U"}
                </div>

                <div className="config-user-user-name">
                    {data.label}
                </div>
                <div className="config-user-user-email">
                    ({data.email})
                </div>
            </div>
        </div>
    );
};

export default class AutomatedReports extends Modal {

    state = {
        reports: [],
        editing: false,
        editingReport: { aggregation: "Plan" },
        isEditingExisting: false,
        aggregationFilter: null,
        investmentProviders: [],
    }

    componentDidMount = async () => {
        await this.loadReports();
    }

    componentDidUpdate = async (prevState) => {
        if (this.state.aggregationFilter === "Providers" && this.state.investmentProviders.length === 0) {
            await this.fetchInvestmentProviders();
        }
    }

    loadReports = async () => {
        const reports = await api.get('/report/schedule/list');
        this.setState({ reports: [...reports.scheduledReports] });
    }

    loadReportFilters = (key, value) => {
        this.setState(prevState => {
            // Creating a deep copy of editingReport, ensuring immutability
            const updatedEditingReport = prevState.editingReport ? {
                ...prevState.editingReport,
                filters: {
                    ...prevState.editingReport.filters,
                    [key]: value.map(v => v.value)
                }
            } : { filters: { [key]: value.map(v => v.value) } };

            return { editingReport: updatedEditingReport };
        });
    }

    // Download document
    downloadDocument = async (doc) => {
        await this.props.loading(1005, "Preparing...");
        const res = await api.download('/document/download?docId=' + doc._id);

        const fileName = doc.name + ".xlsx";

        if (res) download(res, fileName);
        this.props.loaded();
    }

    deleteReport = async (report, idx) => {
        const res = await api.post('/report/schedule/remove', { name: report.name });
        if (res) {
            const reports = [...this.state.reports];
            reports.splice(idx, 1);
            this.setState({ reports: [...reports] });
        }
    }

    editReport = async () => {

        // Make sure we have all the required fields...
        if (!this.state.editingReport?.name || !this.state.editingReport?.aggregation || !this.state.editingReport?.emailTo || !this.state.editingReport?.schedule || !this.state.editingReport?.period) {
            toast.error("Please fill in all the required fields");
            return;
        }
        this.state.editingReport.emailTo = this.state.editingReport.emailTo.map(user => user.value);
        const res = await api.post('/report/schedule/update', { reportDiff: { ...this.state.editingReport } });
        if (res.success) {
            this.setState({ editing: false, editingReport: null });
            this.loadReports();
            toast.success("Scheduled report updated successfully");
        }

    }

    createReport = async () => {

        // if aggregation is not provided set it to Plans
        if (!this.state.editingReport?.aggregation) {
            this.state.editingReport.aggregation = "Plans";
        }

        // if schedule is not provided set it to null
        if (!this.state.editingReport?.schedule) {
            this.state.editingReport.schedule = { every: null, date: null, time: null };
        }

        // Make sure we have all the required fields...
        if (!this.state.editingReport?.name || !this.state.editingReport?.aggregation || !this.state.editingReport?.emailTo || !this.state.editingReport?.schedule || !this.state.editingReport?.period) {
            toast.error("Please fill in all the required fields");
            return;
        }
        this.state.editingReport.emailTo = this.state.editingReport.emailTo.map(user => user.value);

        const res = await api.post('/report/schedule/create', this.state.editingReport);

        if (res.success) {
            this.setState({ editing: false, editingReport: null });
            this.loadReports();
            toast.success("Report scheduled successfully");
        }
    }

    generateReport = async () => {
        
        const transactionMapping = {
            'Deposits': 'deposits',
            'Withdrawal': 'withdrawals',
            'Transfer': 'transfers',
            'Beginning Balance': 'beginningBalance',
            'Ending Balance': 'endingBalance',
            'Income': 'income',
            'Fees': 'fees',
            'Purchases': 'purchases',
            'Sales': 'sales',
            'Other': 'other',
            'Accrued': 'accrued',
            'Reinvestments': 'reinvestments',
            'Gain/Loss': 'gain_loss'
        }

        const requestJSON = {filters: {}, transactionFilters: []};
        requestJSON.reportName = this.state.editingReport?.name;
        requestJSON.aggregateBy = this.state.editingReport?.aggregation ?? "Plans";
        requestJSON.email = this.state.editingReport?.emailTo.map(user => user.email);
        requestJSON.reportingPeriod = this.state.editingReport?.period;
        requestJSON.reportingPeriod.dateFrom = `${this.state.editingReport?.period?.dateFrom?.endYy}-${this.state.editingReport?.period?.dateFrom?.endMm}-${this.state.editingReport?.period?.dateFrom?.endDd}`;
        requestJSON.reportingPeriod.dateTo = `${this.state.editingReport?.period?.dateTo?.endYy}-${this.state.editingReport?.period?.dateTo?.endMm}-${this.state.editingReport?.period?.dateTo?.endDd}`;
        requestJSON.filters.planIds = this.state.editingReport?.filters?.internalPlanId ?? [];
        requestJSON.filters.planNames = this.state.editingReport?.filters?.planName ?? [];
        requestJSON.filters.planTypes = this.state.editingReport?.filters?.planType ?? [];
        requestJSON.filters.status = this.state.editingReport?.filters?.planStatus ?? [];
        requestJSON.filters.participants = this.state.editingReport?.filters?.payee ?? [];
        requestJSON.filters.providers = this.state.editingReport?.filters?.provider ?? [];
        requestJSON.filters.accounts = this.state.editingReport?.filters?.account ?? [];
        requestJSON.filters.planYearEndRange = ""

        requestJSON.transactionFilters = this.state.editingReport?.filters?.transactions?.map(
            t => ({
                field: transactionMapping[t.txType],
                operation: t.operation,
                value: t.amount
            })
        ) ?? [];
        
        const res = await api.post('/report/schedule/generate', {requestJSON:requestJSON});  
        if (res.success) {
            toast.success("Your report will be emailed to you shortly...");
        }   
    }

    generateDays = () => {
        const days = [];
        for (let i = 1; i <= 31; i++) {
            let suffix = 'th';
            if (i === 1 || i === 21 || i === 31) {
                suffix = 'st';
            } else if (i === 2 || i === 22) {
                suffix = 'nd';
            } else if (i === 3 || i === 23) {
                suffix = 'rd';
            }
            days.push(`${i}${suffix}`);
        }
        return days;
    }

    generateHours() {
        const hours = [];
        for (let i = 0; i < 24; i++) {
            const hour = i % 12 === 0 ? 12 : i % 12; // Convert 24-hour time to 12-hour
            const paddedHour = hour < 10 ? `0${hour}` : hour; // Pad with zero if necessary
            const suffix = i < 12 ? 'AM' : 'PM';
            hours.push(`${paddedHour}:00 ${suffix}`);
        }
        return hours;
    }

    fetchInvestmentProviders = async () => {
        const res = await api.get('/tpa/investmentProvider/list');
        if (!res?.success || !res?.investmentProviders)
            return this.setState({ investmentProviders: [] });
        this.setState({
            investmentProviders: res.investmentProviders
        });
    }

    editCustomDateFrom = (type, value) => {
        const updatedEditingReport = this.state.editingReport ? { ...this.state.editingReport } : {};
        updatedEditingReport.period.dateFrom = {
            ...updatedEditingReport.period.dateFrom,
            [type]: value
        };
        this.setState({
            editingReport: updatedEditingReport
        });
    }

    editCustomDateTo = (type, value) => {
        const updatedEditingReport = this.state.editingReport ? { ...this.state.editingReport } : {};
        updatedEditingReport.period.dateTo = {
            ...updatedEditingReport.period.dateTo,
            [type]: value
        };
        this.setState({
            editingReport: updatedEditingReport
        });
    }

    renderContent = () => {

        return (<div className="modal-content modal-center" style={(this.state.editing ? { paddingTop: 60 } : {})}>
            <h1>Automated reports</h1>
            <div>
                Use this function to automatically generate reports at scheduled intervals (for example, once a month). The reports wil be emailed to you when generated.
            </div>
            {!this.state.editing ? <div className='automated-reports-subtitle'>Scheduled reports</div> : ""}
            {this.state?.reports && !this.state.editing ?
                <Sortable
                    className="automated-reports-table"
                    title={null}
                    data={this.state.reports}
                    scrollbar={false}
                    disableSort={true}
                    emptyMessage={"No scheduled reports created"}
                    // Header configuration
                    header={{
                        name: {
                            prop: "Schedule",
                            label: "Schedule",
                            width: 218,
                            marginLeft: 1,
                            paddingLeft: 1,
                            textAlign: "left"
                        },
                        left: [
                            {
                                label: "Report name",
                                prop: "Report name",
                                width: 175
                            },
                            {
                                prop: "Send reports to",
                                label: "Send reports to",
                                width: 200
                            },
                        ],
                        meta: [
                            {
                                prop: "",
                                label: "",
                                width: 10
                            }
                        ],
                        minWidth: 660
                    }}

                    renderItem={(item, idx) => {
                        return (
                            <div
                                key={idx} style={{ display: 'flex', justifyContent: 'flex-start', borderBottom: "1px solid var(--color-border)", marginLeft: 20, marginRight: 20 }}
                            >
                                <div style={{ width: 220 }}>
                                    <div style={{ textAlign: 'start' }}>
                                        {item.schedule.every === null && item.schedule.date === null && item.schedule.time === null ? "Not scheduled" : 
                                        item.schedule.every === "Week" ? `Every ${item.schedule.date}` : item.schedule.every === "Day" ? `Every day at ${item.schedule.time}` : `${item.schedule.date} of each month`}
                                    </div>
                                    <div style={{ width: 550, textAlign: 'start', display: 'flex', marginBottom: 15, marginTop: 20 }}>
                                        {item?.files?.map((file, idx) => {
                                            // only render the 3 last reports
                                            if (idx === 3) {
                                                return (
                                                    <div className='automated-reports-overflow' key={idx} style={{ zIndex: 99999 }}>
                                                        {`${item.files.length - 3} more generated...`}
                                                    </div>
                                                );
                                            }
                                            else if (idx < 3) {
                                                return (
                                                    <div
                                                        key={idx}
                                                        className='automated-reports-file'
                                                        style={{
                                                            backgroundImage: "url('https://static.stax.ai/images/format/xlsx.png')",
                                                            backgroundRepeat: 'no-repeat',
                                                            backgroundPosition: 'left',
                                                            backgroundSize: '15px',
                                                            backgroundPositionX: 5,
                                                            zIndex: 99999,
                                                        }}
                                                        onClick={() => this.downloadDocument(file)}
                                                    >
                                                        <div style={{ paddingLeft: 10 }}>
                                                            {file.name}
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return null;

                                        })}
                                    </div>
                                </div>
                                <div key={Math.random} style={{ width: 240, textAlign: 'start' }}>
                                    {item.name}
                                </div>
                                <div style={{ width: 300 }}>
                                    {
                                        item.emailTo.map((user, idx) => {
                                            const fetchedUser = this.props.team.users.find(u => u._id === user);
                                            if (idx === 0) {
                                                return (
                                                        
                                                        <div key={user}
                                                            className={"config-user-user"}
                                                            style={{ cursor: "auto", backgroundColor: "transparent" }}
                                                            title={item.emailTo.map((user, idx) => {
                                                                return " " + this.props.team.users.find(u => u._id === user)?.name;
                                                            })}
                                                        >
                                                            <div className={"config-user-icon palette-color-" + String(idx % 8)}>
                                                                {fetchedUser?.name ? fetchedUser?.name?.[0].toUpperCase() : "U"}
                                                            </div>

                                                            <div className="config-user-user-name">
                                                                {fetchedUser?.name ? item.emailTo.length > 1 ? `${fetchedUser.name}...` : fetchedUser.name : "User"}
                                                            </div>
                                                        </div>
                                                );
                                            }
                                            return null;
                                        })
                                    }
                                </div>
                                <div
                                    style={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <Button
                                        text={"Edit"}
                                        onClick={() => {// Load the config... 

                                            const config = { ...item };
                                            // We need to udpate the user's we are emailing to so it is rendered properly...  
                                            const emailTo = config.emailTo.map(user => {
                                                // Find the user in the team props... 
                                                const fetchedUser = this.props.team.users.find(u => u._id === user);

                                                return {
                                                    label: fetchedUser?.name,
                                                    value: fetchedUser?._id,
                                                    email: fetchedUser?.email
                                                }
                                            })

                                            config.emailTo = emailTo;
                                            this.setState({ editing: true, editingReport: config, isEditingExisting: true, aggregationFilter: config.aggregation });
                                        }
                                        }
                                        className="plan-editor-account-action"
                                        style={{
                                            backgroundImage: `url(https://static.stax.ai/images/icons/edit.png)`,
                                            backgroundPositionX: 10,
                                        }}
                                    />
                                    <Button
                                        text={"Delete"}
                                        onClick={() => this.deleteReport(item, idx)}
                                        className="plan-editor-account-action"
                                        style={{
                                            color: "var(--color-accent-dark)",
                                            backgroundImage: `url(https://static.stax.ai/images/icons/delete.png)`,
                                        }}
                                    />
                                </div>
                            </div>

                        )
                    }}
                />
                : this.state.editing ?
                    // Editing report
                    <div>

                        <div
                            className='config-form'
                            style={{ width: 780, marginTop: 25 }}
                        >
                            <div
                                className="title-back"
                                style={{ textAlign: 'justify', position: 'unset' }}
                                onClick={() => {
                                    this.setState({ editing: false, editingReport: null })
                                }}
                            >
                                Back
                            </div>
                            {this.state.editing ? this.state.editingReport && this.state.isEditingExisting ?
                                <div className='automated-reports-header'>Edit scheduled report</div> : <div className='automated-reports-header'>Create a new scheduled report</div> : ""
                            }
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div className='automated-reports-editing' style={{ textAlign: 'start', }}>
                                    <div style={{ fontSize: 16, textAlign: 'start', paddingLeft: 10, fontWeight: 600 }}>Report name</div>
                                    <input
                                        type="text"
                                        style={{ width: 540 }}
                                        value={this.state.editingReport?.name ? this.state.editingReport?.name : ""}
                                        onChange={e => {
                                            const updatedEditingReport = this.state.editingReport ? { ...this.state.editingReport } : {};
                                            // Update the name property of the cloned editingReport
                                            updatedEditingReport.name = e.target.value;
                                            // Update the state with the new editingReport object
                                            this.setState({
                                                editingReport: updatedEditingReport
                                            });
                                        }}
                                        placeholder="Type a name for this report..."
                                    />
                                </div>

                                <div className='automated-reports-editing' style={{ marginRight: 10 }}>
                                    <div style={{ fontSize: 16, textAlign: 'start', paddingLeft: 10, fontWeight: 600 }}>Aggregate</div>
                                    <Select
                                        className='automated-reports-filter-selector-container'
                                        classNamePrefix='automated-reports-filter-selector'
                                        value={{
                                            value: this.state.editingReport?.aggregation ?? "Plans",
                                            label: this.state.editingReport?.aggregation ?? "Plans"
                                        }}
                                        options={["Plans", "Accounts"].map(elem => ({
                                            value: elem,
                                            label: elem
                                        }))}
                                        onChange={(e) => {
                                            this.setState({
                                                editingReport: {
                                                    ...this.state.editingReport,
                                                    aggregation: e.value,
                                                    filters: [],
                                                },
                                                aggregationFilter: e.value
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ width: '100%', marginBottom: 40 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Email reports to</div>
                                    <div
                                        className='a'
                                        style={{ paddingRight: 10 }}
                                        onClick={() => this.setState(
                                            {
                                                editingReport: {
                                                    ...this.state.editingReport,
                                                    emailTo: []
                                                }
                                            }
                                        )}
                                    >Clear</div>
                                </div>

                                <Select
                                    className={"filter-select-container"}
                                    classNamePrefix={"multi-select"}
                                    components={{ Option: CustomOption }}
                                    isMulti={true}
                                    placeholder={"Type names or email addresses to find users..."}
                                    options={[...this.props.team.users.map(user => ({
                                        value: user._id,
                                        label: user.name,
                                        email: user.email
                                    }))]}
                                    value={this.state?.editingReport?.emailTo ?? []}
                                    onChange={(value) => {
                                        this.setState({
                                            editingReport: {
                                                ...this.state.editingReport,
                                                emailTo: value
                                            }
                                        })
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ fontSize: 16, fontWeight: 600 }}>Reporting schedule</div>
                                <div
                                        className='a'
                                        style={{ paddingRight: 10 }}
                                        onClick={() => this.setState(
                                            {
                                                editingReport: {
                                                    ...this.state.editingReport,
                                                    schedule: {every: null, date: null, time: null}
                                                }
                                            }
                                        )}
                                    >Clear</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'start', marginTop: 10 }}>
                                <label style={{ marginRight: 15 }}>Run every</label>
                                <div className='automated-reports-editing' style={{ marginRight: 20 }}>
                                    <Select
                                        className='automated-reports-filter-selector-container'
                                        classNamePrefix='automated-reports-filter-selector'
                                        value={{
                                            value: this.state.editingReport?.schedule?.every ?? "",
                                            label: this.state.editingReport?.schedule?.every ?? ""
                                        }}
                                        options={["Month", "Week", "Day"].map(elem => ({
                                            value: elem,
                                            label: elem
                                        }))}
                                        onChange={(e) => {
                                            const config = { ...this.state.editingReport };
                                            const schedule = { ...config.schedule };
                                            schedule.every = e.value;
                                            schedule.date = null;
                                            this.setState({
                                                editingReport: {
                                                    ...this.state.editingReport,
                                                    schedule: schedule
                                                }
                                            })
                                        }}
                                    />
                                </div>
                                <label style={{ marginRight: 15 }}>{this.state.editingReport?.schedule?.every === "Week" ? "on" : this.state.editingReport?.schedule?.every === "Day" ? "" : "on the"}</label>
                                {
                                    this.state.editingReport?.schedule?.every !== "Day" ?
                                        <div className='automated-reports-editing' style={{ marginRight: 20 }}>
                                            <Select
                                                className='automated-reports-filter-selector-container'
                                                classNamePrefix='automated-reports-filter-selector'
                                                value={{
                                                    value: this.state.editingReport?.schedule?.date ?? "",
                                                    label: this.state.editingReport?.schedule?.date ?? ""
                                                }}
                                                options={this.state.editingReport?.schedule?.every === "Week" ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(elem => ({
                                                    value: elem,
                                                    label: elem
                                                })) : this.generateDays().map(elem => ({
                                                    value: elem,
                                                    label: elem
                                                }))
                                                }
                                                onChange={(e) => {
                                                    const config = { ...this.state.editingReport };
                                                    const schedule = { ...config.schedule };
                                                    schedule.date = e.value;
                                                    this.setState({
                                                        editingReport: {
                                                            ...this.state.editingReport,
                                                            schedule: schedule
                                                        }
                                                    })
                                                }}
                                            />
                                        </div>
                                        : ""
                                }

                                <label style={{ marginRight: 15 }}>at</label>
                                <div className='automated-reports-editing' style={{ marginLeft: 10 }}>
                                    <Select
                                        className='automated-reports-filter-selector-container'
                                        classNamePrefix='automated-reports-filter-selector'
                                        value={{
                                            value: this.state.editingReport?.schedule?.time ?? "",
                                            label: this.state.editingReport?.schedule?.time ?? ""
                                        }}
                                        options={this.generateHours().map(elem => ({
                                            value: `${elem} MT`,
                                            label: `${elem} MT`
                                        }))}
                                        onChange={(e) => {
                                            const config = { ...this.state.editingReport };
                                            const schedule = { ...config.schedule };
                                            schedule.time = e.value;
                                            this.setState({
                                                editingReport: {
                                                    ...this.state.editingReport,
                                                    schedule: schedule
                                                }
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 15 }}>
                                <label style={{ fontSize: 16 }}>Reporting period</label>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div className='automated-reports-editing' style={{ marginRight: 15, maxWidth: 190 }}>
                                    <Select
                                        className='automated-reports-filter-selector-container'
                                        classNamePrefix='automated-reports-filter-selector'
                                        value={{
                                            value: this.state.editingReport?.period?.type ?? "",
                                            label: this.state.editingReport?.period?.type ?? ""
                                        }}
                                        options={["Last month", "Last week", "Last quarter", "Last year", "This month", "This quarter", "This year", "Last 30 days", "Last 60 days", "Custom"].map(elem => ({
                                            value: elem,
                                            label: elem
                                        }))}
                                        onChange={(e) => {
                                            this.setState({
                                                editingReport: {
                                                    ...this.state.editingReport,
                                                    period: { type: e.value }
                                                }
                                            })
                                        }}
                                    />
                                </div>
                                {
                                    this.state.editingReport?.period?.type === "Custom" ?
                                        <div style={{ display: 'flex', justifyContent: 'start' }}>
                                            <label style={{ paddingTop: 20, marginRight: 20 }}>From</label>
                                            <PyeCalendarSelector
                                                updateMm={(type, value) => { this.editCustomDateFrom(type, value) }}
                                                updateDd={(type, value) => { this.editCustomDateFrom(type, value) }}
                                                updateYy={(type, value) => { this.editCustomDateFrom(type, value) }}
                                                endMm={this.state.editingReport?.period?.dateFrom?.endMm ?? 1}
                                                endDd={this.state.editingReport?.period?.dateFrom?.endDd ?? 31}
                                                endYy={this.state.editingReport?.period?.dateFrom?.endYy ?? 2023}
                                                onChange={(value) => { }}
                                            />
                                            <label style={{ marginLeft: 15, paddingTop: 20, marginRight: 20 }}>to</label>
                                            <PyeCalendarSelector
                                                updateMm={(type, value) => { this.editCustomDateTo(type, value) }}
                                                updateDd={(type, value) => { this.editCustomDateTo(type, value) }}
                                                updateYy={(type, value) => { this.editCustomDateTo(type, value) }}
                                                endMm={this.state.editingReport?.period?.dateTo?.endMm ?? 12}
                                                endDd={this.state.editingReport?.period?.dateTo?.endDd ?? 31}
                                                endYy={this.state.editingReport?.period?.dateTo?.endYy ?? 2023}
                                                onChange={(value) => { }}
                                            />
                                        </div>
                                        : ""
                                }
                            </div>

                            <div>
                                {
                                    <Filter
                                        filterValues={{ ...this.state.editingReport?.filters ?? {} }}
                                        preLoadAccounts={true}
                                        preLoadProviders={true}
                                        includeTransaction={true}
                                        modal={this.props.modal}
                                        isSearchable={true}
                                        title="Filter"
                                        renderFooterButtons={false}
                                        style={{ width: 770 }}
                                        onFilterChange={this.loadReportFilters}
                                        filterComponents={[
                                            {
                                                dataType: "creatableSelect",
                                                key: "internalPlanId",
                                                label: "Filter by Plan ID",
                                            },
                                            {
                                                dataType: "creatableSelect",
                                                key: "planName",
                                                label: "Filter by Plan name",
                                            },
                                            {
                                                dataType: "creatableSelect",
                                                key: "planType",
                                                label: "Filter by Plan type",
                                            },
                                            {
                                                dataType: "creatableSelect",
                                                key: "planStatus",
                                                label: "Filter by Status",
                                                preSetOptions: [
                                                    {
                                                        value: 'complete',
                                                        label: 'Complete'
                                                    },
                                                    {
                                                        value: 'missing',
                                                        label: 'Missing'
                                                    },
                                                    {
                                                        value: 'to-do',
                                                        label: 'todo'
                                                    },
                                                    {
                                                        value: 'onTrack',
                                                        label: 'on Track'
                                                    },
                                                    {
                                                        value: 'na',
                                                        label: 'N/A'
                                                    }
                                                ]
                                            },
                                            {
                                                dataType: "creatableSelect",
                                                key: "provider",
                                                label: "Filter by Provider",
                                            },
                                            {
                                                dataType: "creatableSelect",
                                                key: "account",
                                                label: "Filter by Account number",
                                            },
                                            {
                                                dataType: "creatableSelect",
                                                key: "payee",
                                                label: "Filter by FBO/Participant name",
                                            },
                                            // {
                                            //     dataType: "calendar",
                                            //     key: "pye",
                                            //     label: "Filter by Plan Year End",
                                            //     style: { width: 300 }
                                            // },
                                            {
                                                dataType: "transaction",
                                                typeOptions: ['Deposits', 'Withdrawal', 'Transfer', 'Beginning Balance', 'Ending Balance', 'Income', 'Fees', 'Purchases', 'Sales', 'Other', 'Accrued', 'Reinvestments', 'Gain/Loss'],
                                                operationOptions: ["is greater than", "is lower than", "is equal to"],
                                            }
                                        ]}
                                        {...this.props}
                                    />
                                }
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    alignItems: 'baseline',
                                    marginTop: 50,
                                }}
                            >
                                <div
                                    className='a'
                                    style={{ marginRight: 'auto', transition: 'none' }}
                                    onClick={() => {
                                        this.setState({ editing: false, editingReport: null })
                                    }}
                                >
                                    ← Discard changes
                                </div>
                                <div
                                    className='a'
                                    style={{ marginRight: 25 }}
                                    onClick={() => {
                                        // new endpoint to gereate the report, call to the worker
                                        this.generateReport();
                                    }}
                                >
                                    Generate report now
                                </div>
                                {this.state.editing ? this.state.editingReport && this.state.isEditingExisting ?
                                    <input
                                        type='submit'
                                        value='SAVE CHANGES'
                                        className='filter-inline-submit'
                                        onClick={async () => {
                                            await this.editReport()
                                        }}
                                    />
                                    :
                                    <input
                                        type='submit'
                                        value='CREATE SCHEDULE'
                                        className='filter-inline-submit'
                                        onClick={async () => {
                                            await this.createReport()
                                        }}
                                    />

                                    : ""
                                }
                            </div>
                        </div>
                    </div>
                    :
                    <Loading
                        type="bubbles"
                        color="var(--color-primary)"
                        height={240}
                        width={60}
                        className="automated-reports-loading"
                    />
            }

            {!this.state.editing ?
                <div style={{ display: 'table' }}>
                    <div
                        className="account-add-icon investment-plan-summary-add-account automated-reports-new"
                        onClick={() => {
                            this.setState({ editing: true, editingReport: null });
                        }}
                    >
                        Create a new schedule report
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            marginLeft: 333,
                            marginTop: 170
                        }}
                    >
                        <input
                            type='submit'
                            value='SAVE AND RETURN'
                            className='filter-inline-submit'
                            style={{}}
                            onClick={() => {
                                this.props.modal();
                            }}
                        />
                    </div>
                </div>
                :
                ""
            }
        </div>);
    }
};